<template>
  <div>
    <div class="document-container">
      <div class="columns">
        <div class="column is-three-quarters">
          <div class="document" style="height:100%">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Process Details
                  </div>
                  <div class="header-subtitle">
                    Displays details about processing.
                  </div>
                </template>
              </EsHeader>
            </div>
            <div class="content">
              <es-form v-if="!isRejected">
                <template #es-form-header>
                  <div class="header-container">
                    <div class="header-elements">
                      <font-awesome-icon
                        :icon="['fas', 'chart-line']"
                        size="lg"
                      />
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Status Statistics
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <table
                    v-if="batchObject"
                    class="table is-fullwidth"
                  >
                    <thead>
                      <tr>
                        <th class="column-header has-text-grey">
                          Match Status
                        </th>
                        <th class="text-align--right has-text-grey"
                          v-if="!loading && recordGroupCategories.length"
                        >
                          Record Count
                        </th>
                      </tr>
                    </thead>
                    <es-loader
                      v-if="loading"
                      style="margin: 5rem"
                    />
                    <es-exception
                      v-if="!loading && !dataLoaded"
                      style="margin: 3.5rem !important"
                      exceptionMessage="No processing status is available."
                    />
                    <tbody v-if="!loading && dataLoaded">
                      <tr
                        class="batch-category has-text-weight-bold"
                        v-for="(category,index) in recordGroupCategories"
                        :key="index"
                      >
                        <td colspan="2" style="border-bottom: 0; padding: 0">
                          <table role="presentation"
                            class="table is-fullwidth"
                            style="margin: 0 1rem 0.5rem 0"
                          >
                            <tbody>
                              <tr class="group-name">
                                <td
                                  class="has-text-weight-bold removeUnderline"
                                  colspan="2"
                                  style="padding-bottom: 0"
                                >
                                  {{ category.group }}
                                  <!-- Near Match System Label needed -->
                                </td>
                              </tr>
                              <tr
                                class="row-border"
                                v-for="(subCategory, index) in category.list.filter(l => l.count)"
                                :key="index"
                              >
                                <td
                                  class ="has-text-grey font-weight-normal"
                                  style="padding: 0 0 0 1rem; color: #666 !important"
                                >
                                {{ subCategory.prefix }}: {{ subCategory.description }}
                                <!-- Near Match System Label needed -->
                                </td>
                                <td
                                  class ="text-align--right font-weight-normal record-count"
                                  style="padding: 0 1rem 0 0"
                                >
                                  {{ subCategory.count }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </es-form>
              <es-form v-if="!isRejected">
                <template #es-form-header>
                  <div class="header-container">
                    <div class="header-elements">
                      <font-awesome-icon :icon="['fas', 'clock']" size="lg" />
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Processing Stage
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <table v-if="batchObject" class="table is-fullwidth">
                    <thead>
                      <th class="column-header has-text-grey">
                        Stage
                      </th>
                      <th class="text-align--right has-text-grey">
                        Processing Date
                      </th>
                    </thead>
                    <tbody
                      v-if="batchObject.processingStages
                        && batchObject.processingStages.length"
                    >
                      <tr
                        class="batch-category"
                        v-for="(item, index) in processingStagesSortAsc"
                        :key="index"
                      >
                        <td class="has-text-grey">
                          {{ item.label }}
                        </td>
                        <td class="text-align--right">
                          {{ item.stageDate | formatDateMMDDYYYYHHMM }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </es-form>
              <es-form
                v-if="isRejected || (batchObject
                  && (batchObject.status ==='ERRORED'
                  || batchObject.status ==='ERRORED_VALIDATION'))"
                style="padding-top: 1.5rem"
              >
                <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon
                          :icon="['fas', 'exclamation-triangle']"
                          size="lg"
                        />
                      </div>
                      <div class="header-elements">
                        <h6 class="title">
                          Errors({{ errorList.length }})
                        </h6>
                      </div>
                    </div>
                  </template>
                <template #es-form>
                  <table class="table is-fullwidth" v-if="batchObject && errorList.length">
                    <thead>
                      <th class="column-header has-text-grey" colspan="2">
                        <span>
                          Error Information:
                        </span>
                        <span class="font-style-italic font-weight-normal">
                          See details below.
                        </span>
                      </th>
                    </thead>
                    <tbody>
                      <tr v-for="(error,index) in errorList" :key="index">
                        <td class ="error-list-font">
                          {{ error }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </es-form>
            </div>
          </div>
        </div>
        <div class="column is-one-quarter">
          <div
            v-if="hasModule('PRSID:DWNLD:DOWNLOAD')
              && isRejected"
            class="document margin-bottom"
          >
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Next Steps
                  </div>
                </template>
              </EsHeader>
            </div>
            <div
              class="is-flex flex-direction--column flex-align--start"
              style="padding: 1rem"
            >
              <div class="margin-bottom--small">
                <router-link
                  :to="{ name: 'PIRejectedBatchInfoDownload' }"
                  aria-label="Download Batch"
                  class="button is-primary has-no-underline"
                  role="button"
                >
                  Download
                </router-link>
              </div>
            </div>
          </div>
          <div v-if="!isRejected" class="document margin-bottom">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Next Steps
                  </div>
                </template>
              </EsHeader>
            </div>
            <div
              class="is-flex flex-direction--column flex-align--start"
              style="padding: 1rem"
            >
              <div
                v-if="canRestartBatch"
                class="margin-bottom--small"
              >
                <router-link
                  :to="{}"
                  @click.native="restartBatch"
                  class="button is-primary has-no-underline"
                >
                  Continue
                </router-link>
              </div>
              <div
                v-if="canRefreshBatch"
                class="margin-bottom--small"
              >
                <router-link
                  :to="{}"
                  @click.native="refreshBatch"
                  class="button is-primary has-no-underline"
                >
                  Refresh
                </router-link>
              </div>
              <div
                v-if="hasModule('PRSID:MATCH:RESOLVENM:VIEW')
                  && !canRestartBatch && batchWithNearMatches"
                class="margin-bottom--small"
              >
                <router-link
                  :to="{
                    path: '/personid/nearMatches',
                    query: { pidBatchId: batchObject.id },
                  }"
                  class="button is-primary has-no-underline"
                >
                  Resolve
                </router-link>
              </div>
              <div
                v-if="hasModule('PRSID:DWNLD:DOWNLOAD')
                  && !canRestartBatch && isDownloadTypesAllowed"
                class="margin-bottom"
              >
                <router-link
                  :to="{ name: 'PIBatchInfoDownload' }"
                  aria-label="Download Batch"
                  class="button is-primary has-no-underline"
                  role="button"
                >
                  Download
                </router-link>
              </div>
              <div
                v-if="hasModule('PRSID:MATCH:HISTORY:VIEW')
                  && recordGroupCategories.length"
              >
                <router-link
                  :to="{
                    path: '/personId/matchHistory/transactions',
                    query: { pidBatchId: batchObject.id },
                  }"
                >
                  View Transactions
                </router-link>
              </div>
            </div>
          </div>
          <div class="document">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    General Information
                  </div>
                </template>
              </EsHeader>
            </div>
            <div style="padding: 1rem 1rem 0.5rem">
              <div class="table-wrraper">
                <table v-if="batchObject" class="form-table table is-fullwidth" role="presentation">
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      {{ getFieldLabel(`PER:${personType}:ASSIGN:DISTRICT_CODE`) }}
                    </td>
                    <td class="text-align--right font">
                      <span v-if="isRejected">
                        --
                      </span>
                      <span v-else-if="districtList.length === 1">
                        {{ batchObject.pidBatchDistrictSchoolAssociation[0].district.location.name }}
                        ({{ batchObject.pidBatchDistrictSchoolAssociation[0].districtCode }})
                      </span>
                      <span v-else>
                        Multiple {{ getFieldLabel(`PER:${personType}:ASSIGN:DISTRICT_CODE`) }}(s/es)
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      {{ getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_CODE`) }}
                    </td>
                    <td class="text-align--right font">
                      <span v-if="isRejected">
                        --
                      </span>
                      <span v-else-if="schoolList.length === 1">
                        {{ batchObject.pidBatchDistrictSchoolAssociation[0].school.location.name }}
                        ({{ batchObject.pidBatchDistrictSchoolAssociation[0].schoolCode }})
                      </span>
                      <span v-else>
                        Multiple {{ getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_CODE`) }}(s/es)
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      Extract Date
                    </td>
                    <td class="text-align--right font">
                      {{ formatExtractDate(batch.extractDate) }}
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      Extract File
                    </td>
                    <td class="text-align--right max-width font">
                      <span
                        v-if="batchObject.submissionTypeCode === 'FILE' ||
                          batchObject.submissionTypeCode === 'AFP'"
                      >
                        {{ batchObject.fileName }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      Transmission ID
                    </td>
                    <td class="text-align--right font">
                      <span v-if="batch.transmissionId">
                        {{ batch.transmissionId }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      {{ getFieldLabel(`PER:${personType}:ASSIGN:SOURCE_CODE`) }}
                    </td>
                    <td class="text-align--right font">
                      <div
                        v-if="invalidSourceCode"
                        class="text-danger"
                      >
                        <font-awesome-icon
                          title="Rejected"
                          class="fa-lg"
                          :icon="['fas', 'exclamation-triangle']"
                        />
                        INVALID<!-- ({{ batch.sourceCode }}) -->
                      </div>
                      <div v-else>
                        {{ batch.sourceCode }}
                      </div>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      Creation Date
                    </td>
                    <td class="text-align--right font">
                      {{ formatDateTime(batchObject.createdDate) }}
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      Creation User
                    </td>
                    <td class="text-align--right font">
                      {{ batch.createdByFirstName +' '+ batch.createdByLastName }}
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey align-bottom">
                      Delimiter (Record)
                    </td>
                    <td class="text-align--right font">
                      <span v-if="batchObject.fileRecordDelimiter">
                        {{ batchObject.fileRecordDelimiter }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey">
                      Delimiter (Header)
                    </td>
                    <td class="text-align--right font">
                      <span v-if="batchObject.fileRecordQualifier">
                        {{ batchObject.fileRecordQualifier }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey" style="border-style: none">
                      File Version
                    </td>
                    <td class="text-align--right font" style="border-style: none">
                      <span v-if="batchObject.fileVersion">
                        {{ batchObject.fileVersion }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left has-text-grey" style="border-style: none">
                      Record Count
                    </td>
                    <td class="text-align--right font" style="border-style: none">
                      <span v-if="batchObject.recordCount">
                        {{ batchObject.recordCount }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faChartLine,
  faClock,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import FieldLabels from '@/mixins/FieldLabels';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faChartLine,
  faClock,
  faExclamationTriangle,
);
export default {
  mixins: [
    FieldLabels,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
  },
  props: [
    'batch',
    'restarting',
  ],
  watch: {
    pidTransactionStatuses(newValue) {
      if (newValue.length > 0) {
        this.allUsersAndRecordGroupCategories();
      }
    },
    recordGroupCategories() {
      if (this.recordGroupCategories.length > 0) {
        this.dataLoaded = true;
      }
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
  },
  computed: {
    ...mapGetters([
      'userPersonTypes',
    ]),
    ...mapGetters('lookups', [
      'pidTransactionStatuses',
      'defaultPersonType',
    ]),
    batchWithNearMatches() {
      if (this.batchObject.status === 'COMPLETED_WITH_PENDING') {
        return true;
      }
      return false;
    },
    canRefreshBatch() {
      if (this.batchObject.status === 'LOADED'
      || this.batchObject.status === 'QUEUED'
      || this.batchObject.status === 'RUNNING_MATCHING'
      || this.batchObject.status === 'VALIDATION_IN_PROGRESS') {
        return true;
      }
      if (this.restarting === true) return true;
      return false;
    },
    canRestartBatch() {
      if (this.batchObject && this.batchObject.submissionTypeCode === 'AFP') {
        return false;
      }
      if (
        (this.batchObject.status === 'ERRORED_MATCHING'
        || this.batchObject.status === 'ERRORED_VALIDATION'
        || this.batchObject.status === 'VALIDATION_COMPLETE'
        ) && this.restarting === false) {
        return true;
      }
      return false;
    },
    districtList() {
      if (this.batchObject.pidBatchDistrictSchoolAssociation) {
        const temp = this.batchObject.pidBatchDistrictSchoolAssociation.map(a => a.districtCode);
        return [...new Set(temp)];
      } return [];
    },
    errorList() {
      if (this.batchObject && this.batchObject.processingMsgs) {
        const splitedErrorList = this.batchObject.processingMsgs.split('\n').filter(val => val !== '');
        return splitedErrorList;
      }
      return [];
    },
    isRejected() {
      return this.batchObject.status === 'REJECTED';
    },
    invalidSourceCode() {
      const { batch } = this;
      return batch && batch.processingMsgs
        && (batch.processingMsgs !== null && batch.processingMsgs !== '')
        && batch.processingMsgs.includes('Source Code is invalid');
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
    schoolList() {
      if (this.batchObject.pidBatchDistrictSchoolAssociation) {
        const temp = this.batchObject.pidBatchDistrictSchoolAssociation.map(a => a.schoolCode);
        return [...new Set(temp)];
      } return [];
    },
    processingStagesSortAsc() {
      if (this.batchObject.processingStages) {
        return this.batchObject.processingStages.slice().sort((a, b) => a.stageDate - b.stageDate);
      } return [];
    },
    allowedExtractDownloadTypes() {
      return this.$store.state.propertiesModule.properties['PERSON-EXTRACT_DOWNLOAD_TYPES_ALLOWED'];
    },
    isDownloadTypesAllowed() {
      if (this.allowedExtractDownloadTypes) {
        return this.allowedExtractDownloadTypes.includes(this.batchObject.submissionTypeCode);
      }
      return null;
    },
  },
  mounted() {
    this.allUsersAndRecordGroupCategories();
  },
  methods: {
    ...mapActions('lookups', [
      'loadPidTransactionStatuses',
    ]),
    allUsersAndRecordGroupCategories() {
      setTimeout(() => {
        const tranStatusGroups = JSON.parse(JSON.stringify(this.pidTransactionStatuses));
        this.batchObject.pidBatchTransStatusCountAssociation.forEach((tranStatus) => {
          tranStatusGroups.forEach((group, outerIndex) => {
            if (group.list.map(n => n.id).flat().includes(tranStatus.status)) {
              tranStatusGroups[outerIndex].list.forEach((s, innerIndex) => {
                if (s.id === tranStatus.status) {
                  tranStatusGroups[outerIndex].list[innerIndex].count = tranStatus.statusCount;
                  tranStatusGroups[outerIndex].showThisGroup = true;
                }
              });
            }
          });
        });
        this.recordGroupCategories = tranStatusGroups.filter(x => x.showThisGroup);
        this.loading = false;
      }, 1000);
    },
    formatDateTime(value) {
      return moment(value).format('MM/DD/YYYY h:mmA');
    },
    formatExtractDate(value) {
      return moment(value).format('MM/DD/YYYY');
    },
    initialData() {
      return {
        batchObject: this.batch,
        dataLoaded: false,
        loading: true,
        recordGroupCategories: [],
        userPersonTypesCodes: null,
      };
    },
    refreshBatch() {
      this.restarting = false;
      this.$emit('refreshTheBatch');
    },
    restartBatch() {
      this.$emit('restartTheBatch');
    },
  },
};
</script>

<style lang="sass" scoped>
  td.align-bottom
    vertical-align: bottom
  .batch-category:last-child
    border-bottom: none
  .batch-category:last-child table .row-border:last-child
    border-bottom: none
  .column-header
    padding-left: 0px
  .error-list-font
    font-size: 12px
    color: #212529
  .font
    font-size: 14px
  .font-style-italic
    font-style: italic !important
  .form-row
    line-height: 2rem
    padding-right: 0.5rem
  .font-weight-normal
    font-weight: normal
  .has-text-light-grey
    color: #999999
  .icon-message
    font-size: 16px
    padding: 2rem 0 0 0
  .loader-center
    text-align: center
    vertical-align: middle
    font-size: 24px
  .margin-bottom
    margin-bottom: 1rem
  .margin-bottom--small
    margin-bottom: 0.5rem
  .max-width
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 20ch
  .record-count
    font-size: 14px
    color: #000 !important
  .removeUnderline
    border-bottom: 1px solid transparent !important
  .row-border
    border-bottom: 1px solid #dee2e6
  .text-align--left
    text-align: left !important
    padding-left: 0
  .text-align--right
    text-align: right !important
    padding-right: 0
  .text-danger
    color: #DC3545
    font-size: 14px
    font-weight: 700
  @media screen and (max-width: 70em)
    .columns
      display: block
      .column
        width: 100%
  .table-wrraper
    overflow-x: auto
  .form-table
    table-layout: fixed
    td
      overflow-wrap: break-word
      white-space: normal
</style>
